// Lista na stronie wyników /{alias}: wiersze i grupy liczone raz na pobranie /splits (bez Vue).
// Wejście: wyniki z magazynu (już posortowane, z czasem z karą, miejscami w kategorii i K/M).
// Grupy: na mecie (miejsca tylko dla klasyfikowanych, remisy dzielą miejsce), w trasie, nie ukończyli
// (DNF bez numeru miejsca), nie wystartowali (DNS). Obsługa i media są poza listą.
import { isSupportRole, isClassified } from './runner-role.js'
import { parseApiTime, formatClock, formatGap } from './polish-time.js'

export const GROUPS = ['finished', 'course', 'dnf', 'dns']

// Tekst do wyszukiwania: małe litery, bez polskich znaków ("Wójcik" znajdzie się po "wojcik").
export function searchKey(text) {
  return String(text || '')
    .toLowerCase()
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .replace(/ł/g, 'l')
    .trim()
}

function groupOf(runner) {
  if (runner.abs) return 'dns'
  if (runner.dnf) return 'dnf'
  if (runner.finish_time || runner.finish_time_gps) return 'finished'
  return 'course'
}

// "M20 2" → 2 (gdy nazwa kategorii się zgadza), inaczej null
function placeAfter(prefix, text) {
  if (!prefix || typeof text !== 'string' || !text.startsWith(`${prefix} `)) return null
  const n = Number(text.slice(prefix.length + 1))
  return Number.isFinite(n) && n > 0 ? n : null
}

function firstLine(text) {
  return String(text || '').split(/\r?\n|\|/)[0].trim()
}

/**
 * @param {Array} results   wyniki z magazynu (kolejność wyników)
 * @param {Object} opts
 *   categories: [{id, name}] (z /splits)
 *   eventStartMs: start wydarzenia (zapas, gdy zawodnik nie ma start_time)
 *   gapOf(runner): {timeGap, pointGap} | null — strata do lidera (magazyn)
 *   hasPoints: wyniki punktowe (rogaining)
 */
export function buildResultsRows(results, { categories = [], eventStartMs = null, gapOf = () => null, hasPoints = false } = {}) {
  const categoryName = new Map(categories.map(c => [String(c.id), c.name || '']))
  const rows = []
  let place = 0
  let prev = null
  for (const r of results) {
    if (isSupportRole(r)) continue
    const group = groupOf(r)
    const classified = isClassified(r)
    let rowPlace = null
    if (group === 'finished' && classified) {
      place += 1
      const tie = prev && prev.elapsedTime != null && prev.elapsedTime === r.elapsedTime && (prev.pointScore || 0) === (r.pointScore || 0)
      rowPlace = tie ? prev.place : place
      prev = { elapsedTime: r.elapsedTime, pointScore: r.pointScore, place: rowPlace }
    }
    const catName = r.category_id != null ? (categoryName.get(String(r.category_id)) || '') : ''
    const ownStartMs = parseApiTime(r.start_time)
    const startMs = ownStartMs ?? eventStartMs
    const finishRaw = r.finish_time || r.finish_time_gps || null
    const finishMs = finishRaw ? parseApiTime(finishRaw) : null
    const gap = group === 'finished' && classified ? gapOf(r) : null
    const penaltyMin = r.penaltySeconds > 0 ? Math.round(r.penaltySeconds / 60) : 0
    rows.push({
      id: r.id,
      number: r.number,
      name: r.name || '',
      bio: r.bio || '',
      gender: r.gender === 'K' || r.gender === 'M' ? r.gender : null,
      categoryId: r.category_id != null ? String(r.category_id) : null,
      categoryName: catName,
      categoryPlace: group === 'finished' ? placeAfter(catName, r.categoryPosition) : null,
      genderPlace: group === 'finished' && r.gender ? placeAfter(r.gender, r.genderPosition) : null,
      group,
      place: rowPlace,
      outOfCompetition: group === 'finished' && !classified,
      points: hasPoints ? (r.pointScore || 0) : null,
      timeText: group === 'finished' && r.timeScore && r.timeScore !== '--:--:--' ? r.timeScore : '',
      penaltyMin,
      gpsOnly: group === 'finished' && !r.finish_time && !!r.finish_time_gps,
      startMs,
      startClock: startMs != null ? formatClock(startMs) : '',
      finishMs,
      finishClock: finishMs != null ? formatClock(finishMs) : '',
      // runners.country = ISO alfa-3 (POL); ekran TV pokazuje kod tylko u zawodników spoza Polski
      country: typeof r.country === 'string' ? r.country.trim().toUpperCase() : '',
      gapText: gap && gap.timeGap > 0 ? formatGap(gap.timeGap) : '',
      pointsGapText: gap && gap.pointGap > 0 ? `−${gap.pointGap} pkt` : '',
      lastPoint: group !== 'finished' && r.lastMarker ? firstLine(r.lastMarker.text) : '',
      search: searchKey(`${r.name || ''} ${r.number ?? ''} ${r.bio || ''}`),
      numberText: String(r.number ?? '')
    })
  }
  return rows
}

// Kategorie obecne na liście, w kolejności z /splits, z liczbą osób (filtr „Kategoria”)
export function categoryOptions(rows, categories = []) {
  const counts = new Map()
  rows.forEach(row => {
    if (row.categoryId) counts.set(row.categoryId, (counts.get(row.categoryId) || 0) + 1)
  })
  return categories
    .filter(c => counts.has(String(c.id)))
    .map(c => ({ id: String(c.id), name: c.name || '', count: counts.get(String(c.id)) }))
}

/**
 * Filtrowanie bez zmiany miejsc (miejsca zostają ogólne).
 * query: numer (dokładny początek) albo fragment nazwiska / nazwy drużyny / składu.
 */
export function filterRows(rows, { query = '', gender = 'all', category = 'all' } = {}) {
  const q = searchKey(query)
  const numeric = /^\d+$/.test(q)
  return rows.filter(row => {
    if (gender !== 'all' && row.gender !== gender) return false
    if (category !== 'all' && row.categoryId !== category) return false
    if (!q) return true
    return numeric ? row.numberText.startsWith(q) || row.search.includes(q) : row.search.includes(q)
  })
}

export function groupRows(rows) {
  const groups = { finished: [], course: [], dnf: [], dns: [] }
  rows.forEach(row => groups[row.group].push(row))
  return groups
}
